import React, { useEffect } from 'react'
import {
	Form,
	Input,
	Select,
	Radio,
	InputNumber,
	Button,
	Card,
	Row,
	Col,
	Space,
	Dropdown,
	Menu,
	Divider,
	Typography,
	Switch,
	Collapse,
} from 'antd'
import {
	MinusCircleOutlined,
	PlusOutlined,
	PhoneOutlined,
	RobotOutlined,
} from '@ant-design/icons'
import {
	allBillingCycle,
	allowedModuleTypes,
	// edgePlatforms,
	PlanDetails,
	BillingCycle,
	AllowedModule,
} from 'types'
import toTileCase from 'utils/toTitleCase'
// import getPlatformLabel from 'utils/getPlatformLabel'
import type { FormListFieldData } from 'antd/lib/form/FormList'

const { Item } = Form
const { Title } = Typography
const { Panel } = Collapse

interface PlanDetailsFormProps {
	form: any
	// eslint-disable-next-line react/require-default-props
	plans?: PlanDetails[]
	// eslint-disable-next-line react/require-default-props, no-unused-vars
	handlePlanSelect?: (event: { key: string }) => void
	parentTitle: string
	parentBillingCycle: BillingCycle
	parentAmount: number
	// parentPlatform: string
	parentStorageAllowed: number
	parentBroadcastLimit: number
	// eslint-disable-next-line react/require-default-props
	disableParentFields?: boolean
	// eslint-disable-next-line react/require-default-props
	disableEditableFields?: boolean
}

const PlanDetailsForm: React.FC<PlanDetailsFormProps> = ({
	form,
	plans = [],
	handlePlanSelect,
	parentTitle,
	parentBillingCycle,
	parentAmount,
	// parentPlatform,
	parentStorageAllowed,
	parentBroadcastLimit,
	disableParentFields = false,
	disableEditableFields = false,
}) => {
	useEffect(() => {
		form.setFieldsValue({ billingCycleAmount: parentAmount })
	}, [parentAmount, form])

	// eslint-disable-next-line prettier/prettier
	const watchAllowedModules: AllowedModule[] | undefined = Form.useWatch(
		'allowedModules',
		form
	)
	const billingLabel: string =
		parentBillingCycle === 'MONTHLY' ? 'Per Month' : 'Per Year'

	return (
		<>
			<Divider />
			<Row justify='space-between' style={{ marginBottom: '1rem' }}>
				<Col>
					<Title level={4}>Plan Details</Title>
				</Col>
				{plans.length > 0 && handlePlanSelect && (
					<Col>
						<Dropdown
							overlay={
								<Menu onClick={handlePlanSelect}>
									{plans.map(plan => (
										<Menu.Item key={plan.id}>{plan.title}</Menu.Item>
									))}
								</Menu>
							}
						>
							<Button>
								<Space>Select Plan</Space>
							</Button>
						</Dropdown>
					</Col>
				)}
			</Row>

			<Item label='Title' name='title' initialValue={parentTitle}>
				<Input disabled={disableParentFields} />
			</Item>

			<Item
				label='Billing Cycle'
				name='billingCycle'
				// initialValue={parentBillingCycle}
			>
				<Radio.Group
					disabled={disableParentFields}
					options={allBillingCycle.map(b => ({
						value: b,
						label: toTileCase(b),
					}))}
				/>
			</Item>

			<Item
				label={billingLabel}
				name='billingCycleAmount'
				initialValue={parentAmount}
			>
				<InputNumber
					disabled={disableParentFields}
					style={{ width: '100%' }}
					min={0}
				/>
			</Item>
			<Item
				label='Platform'
				name='platform'
				initialValue='DASH_CX'
				rules={[{ required: true, message: 'Platform is required' }]}
			>
				<Select>
					<Select.Option value='DASH_CX'>DashCX</Select.Option>
				</Select>
			</Item>

			<Item
				label='Storage Allowed (GB)'
				name='storageAllowed'
				initialValue={(parentStorageAllowed / (1024 * 1024 * 1024)).toString()}
				rules={[{ required: true, message: 'Storage allowed is required' }]}
			>
				<Input disabled={disableEditableFields} />
			</Item>

			<Item
				label='Broadcast Limit'
				name='broadcastLimit'
				initialValue={parentBroadcastLimit}
				rules={[{ required: true, message: 'Broadcast Limit is required' }]}
			>
				<Input disabled={disableEditableFields} />
			</Item>
			<h4>Modules</h4>
			<Form.List name='allowedModules'>
				{(fields: FormListFieldData[], { add, remove }) => (
					<>
						{fields.map(field => {
							const currentModuleType = watchAllowedModules?.[field.name]?.type ?? ''

							return (
								<Form.Item key={field.key} style={{ marginBottom: '10px' }}>
									<Card>
										<Row gutter={16} align='middle'>
											<Col span={12}>
												<Form.Item
													label='Module'
													name={[field.name, 'type']}
													rules={[{ required: true, message: 'Module Type is required' }]}
												>
													<Select
														disabled={disableEditableFields}
														showSearch
														optionFilterProp='label'
														options={allowedModuleTypes.map(type => {
															// eslint-disable-next-line prettier/prettier
															const isTypeSelectedElsewhere = watchAllowedModules?.some(
																(mod, idx) =>
																	mod.type === type &&
																	idx !== field.name &&
																	mod.type !== currentModuleType
															)

															return {
																label: toTileCase(type),
																value: type,
																disabled: !!isTypeSelectedElsewhere,
															}
														})}
													/>
												</Form.Item>
											</Col>
											<Col span={8}>
												<Form.Item
													label='Limit (0 = Unlimited)'
													name={[field.name, 'count']}
													rules={[{ required: true, message: 'Module limit is required' }]}
												>
													<InputNumber disabled={disableEditableFields} min={0} />
												</Form.Item>
											</Col>

											<Col span={24}>
												<Button
													htmlType='button'
													type='default'
													danger
													icon={<MinusCircleOutlined />}
													onClick={() => remove(field.name)}
													block
													disabled={disableEditableFields}
												>
													Remove Module
												</Button>
											</Col>
										</Row>
									</Card>
								</Form.Item>
							)
						})}

						<Form.Item>
							<Button
								htmlType='button'
								icon={<PlusOutlined />}
								onClick={() => add({ type: '', count: 0 })}
								style={{ width: '100%' }}
								disabled={disableEditableFields}
							>
								Add Module
							</Button>
						</Form.Item>
					</>
				)}
			</Form.List>

			<Divider />

			{/* Voice Calling Configuration */}
			<Collapse>
				<Panel
					header={
						<Space>
							<PhoneOutlined />
							<Title level={5} style={{ margin: 0 }}>
								Voice Calling Configuration
							</Title>
						</Space>
					}
					key='voice-calling'
				>
					<Card>
						<Row gutter={16}>
							<Col span={24}>
								<Form.Item
									label='Enable Voice Calling'
									name={['voiceCalling', 'enabled']}
									valuePropName='checked'
									initialValue={false}
								>
									<Switch disabled={disableEditableFields} />
								</Form.Item>
							</Col>
						</Row>

						<Form.Item
							noStyle
							shouldUpdate={(prevValues, currentValues) =>
								prevValues.voiceCalling?.enabled !== currentValues.voiceCalling?.enabled
							}
						>
							{({ getFieldValue }) => {
								const voiceEnabled = getFieldValue(['voiceCalling', 'enabled'])

								if (!voiceEnabled) return null

								return (
									<>
										<Title level={5}>One-time Setup Costs (Reference Only)</Title>
										<Row gutter={16}>
											<Col span={12}>
												<Form.Item
													label='Virtual Number Setup Fee'
													name={['voiceCalling', 'pricing', 'virtualNumberSetupFee']}
													rules={[{ required: true, message: 'Setup fee is required' }]}
													initialValue={3000}
												>
													<InputNumber
														disabled={disableEditableFields}
														min={0}
														style={{ width: '100%' }}
													/>
												</Form.Item>
											</Col>
										</Row>

										<Title level={5}>Monthly Rental Costs (Reference Only)</Title>
										<Row gutter={16}>
											<Col span={8}>
												<Form.Item
													label='Virtual Number Monthly Rental'
													name={['voiceCalling', 'pricing', 'virtualNumberMonthlyRental']}
													rules={[{ required: true, message: 'Monthly rental is required' }]}
													initialValue={200}
												>
													<InputNumber
														disabled={disableEditableFields}
														min={0}
														style={{ width: '100%' }}
													/>
												</Form.Item>
											</Col>
											<Col span={8}>
												<Form.Item
													label='Per Channel Monthly Rental'
													name={['voiceCalling', 'pricing', 'perChannelMonthlyRental']}
													rules={[
														{ required: true, message: 'Per channel rental is required' },
													]}
													initialValue={650}
												>
													<InputNumber
														disabled={disableEditableFields}
														min={0}
														style={{ width: '100%' }}
													/>
												</Form.Item>
											</Col>
											<Col span={8}>
												<Form.Item
													label='Number of Channels'
													name={['voiceCalling', 'pricing', 'numberOfChannels']}
													rules={[
														{ required: true, message: 'Number of channels is required' },
													]}
													initialValue={1}
												>
													<InputNumber
														disabled={disableEditableFields}
														min={1}
														max={10}
														style={{ width: '100%' }}
													/>
												</Form.Item>
											</Col>
										</Row>

										<Title level={5}>Per-Call Usage (Active Billing)</Title>
										<Row gutter={16}>
											<Col span={12}>
												<Form.Item
													label='Inbound Per Minute Rate (incoming leg)'
													name={['voiceCalling', 'pricing', 'inboundPerMinuteRate']}
													rules={[{ required: true, message: 'Inbound rate is required' }]}
													initialValue={0.2}
													tooltip='Rate for incoming call leg (customer → system). Used for AI/IVR calls'
												>
													<InputNumber
														disabled={disableEditableFields}
														min={0}
														step={0.01}
														style={{ width: '100%' }}
													/>
												</Form.Item>
											</Col>
											<Col span={12}>
												<Form.Item
													label='Outbound Per Minute Rate (outgoing leg)'
													name={['voiceCalling', 'pricing', 'outboundPerMinuteRate']}
													rules={[{ required: true, message: 'Outbound rate is required' }]}
													initialValue={0.2}
													tooltip='Rate for outgoing call leg (system → agent). Used for bridging to agents'
												>
													<InputNumber
														disabled={disableEditableFields}
														min={0}
														step={0.01}
														style={{ width: '100%' }}
													/>
												</Form.Item>
											</Col>
										</Row>
									</>
								)
							}}
						</Form.Item>
					</Card>
				</Panel>

				{/* AI Services Configuration */}
				<Panel
					header={
						<Space>
							<RobotOutlined />
							<Title level={5} style={{ margin: 0 }}>
								AI Services Configuration
							</Title>
						</Space>
					}
					key='ai-services'
				>
					<Card>
						<Row gutter={16}>
							<Col span={24}>
								<Form.Item
									label='Enable AI Services'
									name={['aiServices', 'enabled']}
									valuePropName='checked'
									initialValue={false}
								>
									<Switch disabled={disableEditableFields} />
								</Form.Item>
							</Col>
						</Row>

						<Form.Item
							noStyle
							shouldUpdate={(prevValues, currentValues) =>
								prevValues.aiServices?.enabled !== currentValues.aiServices?.enabled
							}
						>
							{({ getFieldValue }) => {
								const aiEnabled = getFieldValue(['aiServices', 'enabled'])

								if (!aiEnabled) return null

								return (
									<Row gutter={16}>
										<Col span={12}>
											<Form.Item
												label='Currency'
												name={['aiServices', 'pricing', 'currency']}
												rules={[{ required: true, message: 'Currency is required' }]}
												initialValue='INR'
											>
												<Select disabled={disableEditableFields}>
													<Select.Option value='INR'>INR</Select.Option>
													<Select.Option value='USD'>USD</Select.Option>
												</Select>
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												label='Per Token Rate'
												name={['aiServices', 'pricing', 'perTokenRate']}
												rules={[{ required: true, message: 'Per token rate is required' }]}
												initialValue={0.002}
												tooltip='Rate charged per AI token (deducted from wallet)'
											>
												<InputNumber
													disabled={disableEditableFields}
													min={0}
													step={0.0001}
													style={{ width: '100%' }}
												/>
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												label='Per Transcription Minute Rate'
												name={['aiServices', 'pricing', 'perTranscriptionMinuteRate']}
												initialValue={0}
												tooltip='Per minute of transcribed audio (rounded up). Set 0 for BYOK / no charge.'
											>
												<InputNumber
													disabled={disableEditableFields}
													min={0}
													step={0.01}
													style={{ width: '100%' }}
												/>
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												label='Per Document Intelligence Unit Rate'
												name={['aiServices', 'pricing', 'perDocumentIntelligenceUnitRate']}
												initialValue={0}
												tooltip='Per PDF page or image processed. Set 0 for BYOK / no charge.'
											>
												<InputNumber
													disabled={disableEditableFields}
													min={0}
													step={0.01}
													style={{ width: '100%' }}
												/>
											</Form.Item>
										</Col>
									</Row>
								)
							}}
						</Form.Item>
					</Card>
				</Panel>
			</Collapse>
		</>
	)
}

export default PlanDetailsForm
